import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Send, Users, Hash, Pin, MoreVertical } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface Message {
  id: string;
  content: string;
  user_id: string;
  username: string;
  created_at: string;
  is_pinned: boolean;
  reactions: Record<string, string[]>;
}

interface GroupChatroomProps {
  groupId: string;
  groupName: string;
  userRole?: string;
}

export function GroupChatroom({ groupId, groupName, userRole }: GroupChatroomProps) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [onlineCount, setOnlineCount] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (groupId) {
      fetchMessages();
      subscribeToMessages();
      subscribeToPresence();
    }
  }, [groupId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchMessages = async () => {
    try {
      // For now, we'll create a default channel for the group
      const { data: channelData } = await supabase
        .from("channels")
        .select("id")
        .eq("group_id", groupId)
        .eq("name", "general")
        .single();

      let channelId = channelData?.id;

      // Create default channel if it doesn't exist
      if (!channelId) {
        const { data: newChannel, error: channelError } = await supabase
          .from("channels")
          .insert({
            name: "general",
            description: "General discussion",
            group_id: groupId,
            created_by: user?.id
          })
          .select()
          .single();

        if (channelError) throw channelError;
        channelId = newChannel.id;
      }

      // Fetch messages from the channel
      const { data: messagesData, error } = await supabase
        .from("messages")
        .select(`
          id,
          content,
          user_id,
          created_at,
          is_pinned,
          profiles!messages_user_id_fkey (username)
        `)
        .eq("channel_id", channelId)
        .order("created_at", { ascending: true })
        .limit(50);

      if (error) throw error;

      const formattedMessages = messagesData?.map(msg => ({
        id: msg.id,
        content: msg.content,
        user_id: msg.user_id,
        username: (msg.profiles as any)?.username || "Unknown User",
        created_at: msg.created_at,
        is_pinned: msg.is_pinned || false,
        reactions: {}
      })) || [];

      setMessages(formattedMessages);
    } catch (error) {
      console.error("Error fetching messages:", error);
      toast.error("Failed to load messages");
    }
  };

  const subscribeToMessages = () => {
    const channel = supabase
      .channel(`group_${groupId}_messages`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `channel_id=in.(select id from channels where group_id='${groupId}')`
        },
        (payload) => {
          console.log('New message received:', payload);
          fetchMessages(); // Refresh messages for real-time updates
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `channel_id=in.(select id from channels where group_id='${groupId}')`
        },
        (payload) => {
          console.log('Message updated:', payload);
          fetchMessages(); // Refresh for pin/delete updates
        }
      )
      .subscribe((status) => {
        console.log('Messages subscription status:', status);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const subscribeToPresence = () => {
    const channel = supabase.channel(`group_${groupId}_presence`);
    
    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        setOnlineCount(Object.keys(state).length);
      })
      .on('presence', { event: 'join' }, () => {
        const state = channel.presenceState();
        setOnlineCount(Object.keys(state).length);
      })
      .on('presence', { event: 'leave' }, () => {
        const state = channel.presenceState();
        setOnlineCount(Object.keys(state).length);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED' && user) {
          await channel.track({
            user_id: user.id,
            online_at: new Date().toISOString(),
          });
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !user) return;

    setIsLoading(true);
    try {
      // Get or create the default channel
      const { data: channelData } = await supabase
        .from("channels")
        .select("id")
        .eq("group_id", groupId)
        .eq("name", "general")
        .single();

      const channelId = channelData?.id;
      if (!channelId) {
        toast.error("Channel not found");
        return;
      }

      const { error } = await supabase
        .from("messages")
        .insert({
          content: newMessage.trim(),
          channel_id: channelId,
          user_id: user.id
        });

      if (error) throw error;

      setNewMessage("");
      toast.success("Message sent!");
    } catch (error: any) {
      console.error("Error sending message:", error);
      toast.error(error.message || "Failed to send message");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handlePinMessage = async (messageId: string) => {
    if (userRole !== "admin" && userRole !== "moderator") {
      toast.error("Only admins and moderators can pin messages");
      return;
    }

    try {
      const { error } = await supabase
        .from("messages")
        .update({ is_pinned: true })
        .eq("id", messageId);

      if (error) throw error;
      
      toast.success("Message pinned!");
      fetchMessages();
    } catch (error) {
      console.error("Error pinning message:", error);
      toast.error("Failed to pin message");
    }
  };

  const handleDeleteMessage = async (messageId: string, messageUserId: string) => {
    const canDelete = user?.id === messageUserId || userRole === "admin" || userRole === "moderator";
    
    if (!canDelete) {
      toast.error("You can only delete your own messages");
      return;
    }

    try {
      const { error } = await supabase
        .from("messages")
        .update({ is_deleted: true, deleted_by: user?.id })
        .eq("id", messageId);

      if (error) throw error;
      
      toast.success("Message deleted!");
      fetchMessages();
    } catch (error) {
      console.error("Error deleting message:", error);
      toast.error("Failed to delete message");
    }
  };

  const addReaction = async (messageId: string, emoji: string) => {
    // Simple reaction system - you can expand this
    toast.info(`Reacted with ${emoji}! (Feature in development)`);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-200px)] max-w-4xl mx-auto">
      {/* Chat Header */}
      <Card className="mb-4">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Hash className="h-5 w-5 text-primary" />
              <CardTitle className="text-xl font-semibold">
                {groupName} - General Chat
              </CardTitle>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="secondary" className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {onlineCount} online
              </Badge>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Messages Container */}
      <Card className="flex-1 flex flex-col min-h-0">
        <CardContent className="flex-1 p-4 overflow-y-auto space-y-4">
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <div className="text-center">
                <Hash className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <p className="text-lg font-medium">Welcome to {groupName}!</p>
                <p className="text-sm">Be the first to start the conversation.</p>
              </div>
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors group ${
                  message.is_pinned ? "bg-primary/5 border border-primary/20" : ""
                }`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm">
                      @{message.username}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                    </span>
                    {message.is_pinned && (
                      <Pin className="h-3 w-3 text-primary" />
                    )}
                  </div>
                  <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                    {message.content}
                  </p>
                  
                  {/* Reaction buttons */}
                  <div className="flex items-center gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 px-2 text-xs"
                      onClick={() => addReaction(message.id, "👍")}
                    >
                      👍
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 px-2 text-xs"
                      onClick={() => addReaction(message.id, "❤️")}
                    >
                      ❤️
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 px-2 text-xs"
                      onClick={() => addReaction(message.id, "😂")}
                    >
                      😂
                    </Button>
                  </div>
                </div>
                
                {/* Message actions dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="dropdown-content">
                    {(userRole === "admin" || userRole === "moderator") && (
                      <>
                        <DropdownMenuItem onClick={() => handlePinMessage(message.id)}>
                          <Pin className="h-4 w-4 mr-2" />
                          Pin Message
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    {(user?.id === message.user_id || userRole === "admin" || userRole === "moderator") && (
                      <DropdownMenuItem 
                        onClick={() => handleDeleteMessage(message.id, message.user_id)}
                        className="text-destructive"
                      >
                        Delete Message
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </CardContent>

        {/* Message Input */}
        <div className="p-4 border-t">
          <div className="flex gap-2">
            <Input
              placeholder={`Message ${groupName}...`}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              className="flex-1"
            />
            <Button 
              onClick={sendMessage} 
              disabled={!newMessage.trim() || isLoading}
              size="sm"
              className="btn-mobile"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}